import { BRAND } from '@verlo/shared/lib/brand';
import { useEffect, useState } from 'react';
import { Wrench, TrendingUp, TrendingDown, Minus, ExternalLink, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { formatMetric } from './formatters';

// Reusable mini-bar chart for hour-of-day + contributors
const MiniBarRow = ({ label, value, max, format, accent = BRAND.verlo }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <li className="flex items-center gap-3 text-[12.5px]">
      <span className="w-16 text-ink-soft shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-paper rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} />
      </div>
      <span className="w-20 text-right font-mono-display text-ink shrink-0">{formatMetric(value, format)}</span>
    </li>
  );
};

const Trend14Chart = ({ points, format }) => {
  if (!points?.length) return null;
  const w = 320, h = 90, pad = 8;
  const values = points.map((p) => p.value);
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const step = (w - pad * 2) / Math.max(values.length - 1, 1);
  const pts = values.map((v, i) => [pad + i * step, pad + (h - pad * 2) * (1 - (v - min) / range)]);
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
  const areaPath = `${linePath} L ${pad + (values.length - 1) * step} ${h - pad} L ${pad} ${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" data-testid="drill-trend-chart">
      <path d={areaPath} fill="rgba(32,27,95,0.08)" />
      <path d={linePath} fill="none" stroke={BRAND.verlo} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 2.5 : 1.5} fill={BRAND.verlo} data-testid={`drill-bucket-${i}`} />
      ))}
      {/* Y labels */}
      <text x="2" y="12" fontSize="8" fill="#94a3b8" className="font-mono-display">{formatMetric(max, format)}</text>
      <text x="2" y={h - 4} fontSize="8" fill="#94a3b8" className="font-mono-display">{formatMetric(min, format)}</text>
    </svg>
  );
};

const DrillPanelContent = ({ kpi, range, site }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    reportsIntelApi
      .kpiDrilldown(kpi.key, range, site)
      .then((res) => { if (!cancelled) setData(res.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kpi.key, range, site]);

  if (loading) {
    return <div className="p-6 text-center text-[12px] text-ink-faint">Loading drill-down…</div>;
  }
  if (!data) {
    return <div className="p-6 text-center text-[12px] text-signal-stop">Could not load detail.</div>;
  }
  // Percent-ratio KPIs (labour %, GP %…) have no real per-order time series — the
  // drill returns empty distributions rather than a synthetic one (P5-DRILL).
  if (data.is_empty || !data.trend_14d?.length) {
    return (
      <div className="p-8 text-center" data-testid="drill-empty">
        <BarChart3 size={24} className="text-ink-faint mx-auto mb-2" />
        <p className="text-[12px] text-ink-soft">No time-series breakdown for this metric.</p>
      </div>
    );
  }

  const maxHour = data.hour_breakdown.length ? Math.max(...data.hour_breakdown.map((h) => h.value)) : 0;
  const maxContrib = data.contributors.length ? Math.max(...data.contributors.map((c) => c.value)) : 0;

  const yoyDelta = data.yoy?.delta_pct;
  const YoyIcon = yoyDelta == null ? Minus : yoyDelta > 0 ? TrendingUp : TrendingDown;
  const yoyColor = yoyDelta == null ? 'text-ink-faint' : yoyDelta > 0 ? 'text-signal-ok' : 'text-signal-stop';

  return (
    <div className="space-y-6 mt-4" data-testid="drill-detail">
      {/* Headline */}
      <div className="bg-surface-2 border border-hairline rounded-lg p-5">
        <div className="text-[11px] uppercase tracking-wider text-ink-faint font-semibold mb-1">Current</div>
        <div className="font-mono-display text-4xl font-medium text-ink">{formatMetric(data.value, data.format)}</div>
        <div className="flex gap-5 mt-2 flex-wrap">
          {data.previous != null && (
            <div className="text-[12.5px] text-ink-soft">
              <span className="uppercase tracking-wider font-semibold text-ink-faint">Previous </span>
              <span className="font-mono-display text-ink-soft ml-1">{formatMetric(data.previous, data.format)}</span>
              {data.delta_pct != null && (
                <span className={`ml-1 font-mono-display ${data.delta_pct > 0 ? 'text-signal-ok' : 'text-signal-stop'}`}>
                  ({data.delta_pct > 0 ? '+' : ''}{data.delta_pct.toFixed(1)}%)
                </span>
              )}
            </div>
          )}
          {data.target != null && (
            <div className="text-[12.5px] text-ink-soft">
              <span className="uppercase tracking-wider font-semibold text-ink-faint">Target </span>
              <span className="font-mono-display text-ink-soft ml-1">{data.format === 'percent' ? `${data.target}%` : data.target}</span>
            </div>
          )}
        </div>
      </div>

      {/* 14-day trend */}
      <div data-testid="drill-section-trend">
        <h4 className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">14-day trend</h4>
        <div className="bg-white border border-hairline rounded-lg p-3">
          <Trend14Chart points={data.trend_14d} format={data.format} />
          <div className="flex justify-between text-[12.5px] text-ink-faint font-mono-display mt-1">
            <span>{new Date(data.trend_14d[0].date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
            <span>{new Date(data.trend_14d[data.trend_14d.length - 1].date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
          </div>
        </div>
      </div>

      {/* Hour-of-day */}
      <div data-testid="drill-section-hours">
        <h4 className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">By hour of day</h4>
        <div className="bg-white border border-hairline rounded-lg p-3">
          <ul className="space-y-1.5">
            {data.hour_breakdown.map((h) => (
              <MiniBarRow key={h.hour} label={h.hour} value={h.value} max={maxHour} format={data.format} />
            ))}
          </ul>
        </div>
      </div>

      {/* Top contributors */}
      {data.contributors.length > 0 && (
        <div data-testid="drill-section-contributors">
          <h4 className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">Top contributors</h4>
          <div className="bg-white border border-hairline rounded-lg p-3">
            <ul className="space-y-1.5">
              {data.contributors.map((c) => (
                <MiniBarRow key={c.label} label={c.label} value={c.value} max={maxContrib} format={data.format} accent="#4F46E5" />
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* YoY */}
      <div data-testid="drill-section-yoy">
        <h4 className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">Same period last year</h4>
        <div className="bg-white border border-hairline rounded-lg p-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-ink-soft uppercase tracking-wider font-semibold">Last year</div>
            <div className="font-mono-display text-xl text-ink">{formatMetric(data.yoy.value, data.format)}</div>
          </div>
          <div className={`inline-flex items-center gap-1 ${yoyColor} text-sm font-semibold font-mono-display`}>
            <YoyIcon size={14} />
            {yoyDelta != null && (yoyDelta > 0 ? '+' : '')}{yoyDelta?.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Builder deep-link */}
      <div className="pt-2">
        <Link
          to="/back-office/reports/builder"
          className="inline-flex items-center gap-2 text-[12px] font-semibold text-verlo hover:text-verlo-hover"
          data-testid="drill-open-builder"
        >
          <Wrench size={13} />
          Open in Report Builder
          <ExternalLink size={11} className="opacity-60" />
        </Link>
      </div>
    </div>
  );
};

export default DrillPanelContent;
